import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Play,
  RotateCcw,
  CheckCircle2,
  Circle,
  Bot,
  Sparkles,
  Mic,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DEMO_SCENARIOS,
  type DemoScenario,
  type DemoEvent,
  type DemoQuestion,
} from '@/data/acme-demo';
import { useDemoTimeline } from '@/hooks/useDemoTimeline';
import { VideoOverlay } from '@/components/demo/VideoOverlay';
import { PressHarderBanner } from '@/components/demo/PressHarderBanner';

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const FRAMEWORK_CHIP_STYLES: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  SEE: { bg: 'bg-[#4DA6D4]/10', text: 'text-[#4DA6D4]', border: 'border-[#4DA6D4]/30' },
  CHANGE: { bg: 'bg-[#9B4FD4]/10', text: 'text-[#9B4FD4]', border: 'border-[#9B4FD4]/30' },
  ADAPT: { bg: 'bg-[#5BBFB5]/10', text: 'text-[#5BBFB5]', border: 'border-[#5BBFB5]/30' },
  USE: { bg: 'bg-[#C47B9F]/10', text: 'text-[#C47B9F]', border: 'border-[#C47B9F]/30' },
  LEARN: { bg: 'bg-[#5C6BC0]/10', text: 'text-[#5C6BC0]', border: 'border-[#5C6BC0]/30' },
  EXIT: { bg: 'bg-[#C4785A]/10', text: 'text-[#C4785A]', border: 'border-[#C4785A]/30' },
};

function StreamingText({ text, wordsPerChunk = 1, intervalMs = 250 }: { text: string; wordsPerChunk?: number; intervalMs?: number }) {
  const [visibleCount, setVisibleCount] = useState(0);
  const words = useMemo(() => text.split(/\s+/), [text]);

  useEffect(() => {
    setVisibleCount(0);
    let i = 0;
    const timer = setInterval(() => {
      i += wordsPerChunk;
      if (i >= words.length) {
        setVisibleCount(words.length);
        clearInterval(timer);
      } else {
        setVisibleCount(i);
      }
    }, intervalMs);
    return () => clearInterval(timer);
  }, [words, wordsPerChunk, intervalMs]);

  return (
    <span>
      {words.slice(0, visibleCount).join(' ')}
      {visibleCount < words.length && (
        <span className="inline-block w-1.5 h-3 ml-0.5 bg-foreground/40 animate-pulse rounded-sm align-middle" />
      )}
    </span>
  );
}

function FrameworkChip({ tag }: { tag: string }) {
  const style = FRAMEWORK_CHIP_STYLES[tag];
  if (!style) return null;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wider uppercase ${style.bg} ${style.text} ${style.border}`}
    >
      {tag}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Types for runtime state
// ---------------------------------------------------------------------------

interface TranscriptLine {
  id: string;
  speaker: string;
  text: string;
}

interface AINote {
  id: string;
  timestamp: string;
  category: string;
  content: string;
  question?: string;
  resolved?: boolean;
}

interface FollowUpSuggestion {
  question: string;
  context: string;
  criterion: string;
}

// ---------------------------------------------------------------------------
// Main Demo Page
// ---------------------------------------------------------------------------

export default function VendorCallDemoPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const liveCategoryRef = useRef<HTMLDivElement>(null);

  const [activeScenario, setActiveScenario] = useState<DemoScenario>(
    DEMO_SCENARIOS[0],
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [demoStarted, setDemoStarted] = useState(false);
  const [demoEnded, setDemoEnded] = useState(false);

  const [answeredQuestions, setAnsweredQuestions] = useState<
    Record<string, string>
  >({});
  const [transcriptLines, setTranscriptLines] = useState<TranscriptLine[]>([]);
  const [aiNotes, setAiNotes] = useState<AINote[]>([]);
  const [pressHarder, setPressHarder] = useState<{
    criterion: string;
    message: string;
  } | null>(null);
  const [followUp, setFollowUp] = useState<FollowUpSuggestion | null>(null);
  const [followUpVisible, setFollowUpVisible] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [leftWidthPercent, setLeftWidthPercent] = useState(20);
  const isDraggingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Build initial pre-answered map from scenario data
  const preAnsweredMap = useMemo(() => {
    if (!activeScenario) return {};
    const map: Record<string, string> = {};
    for (const q of activeScenario.questions) {
      if (q.preAnswered && q.preAnswer) {
        map[q.id] = q.preAnswer;
      }
    }
    return map;
  }, [activeScenario]);

  // Merged answers: pre-answered + live-answered
  const allAnswers = useMemo(
    () => ({ ...preAnsweredMap, ...answeredQuestions }),
    [preAnsweredMap, answeredQuestions],
  );

  // Expand all categories that have questions when scenario is selected
  useEffect(() => {
    if (!activeScenario) return;
    setExpandedCategories(new Set(activeScenario.categoryOrder));
  }, [activeScenario]);

  const formatTimestamp = useCallback(() => {
    const video = videoRef.current;
    if (!video) return '00:00';
    const rel = Math.max(0, Math.floor(video.currentTime));
    const m = Math.floor(rel / 60);
    const s = rel % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }, []);

  const handleEvent = useCallback(
    (event: DemoEvent) => {
      switch (event.type) {
        case 'transcript_line':
          if (event.speaker && event.text) {
            setTranscriptLines((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                speaker: event.speaker!,
                text: event.text!,
              },
            ]);
          }
          break;

        case 'answer_question':
          if (event.questionId && event.answer) {
            setAnsweredQuestions((prev) => ({
              ...prev,
              [event.questionId!]: event.answer!,
            }));
          }
          break;

        case 'ai_note':
          if (event.note) {
            const isResolution = event.note.content.startsWith('✅');
            setAiNotes((prev) => {
              const updated = isResolution
                ? prev.map((n) =>
                    n.category === event.note!.category && n.question
                      ? { ...n, resolved: true }
                      : n,
                  )
                : prev;
              return [
                ...updated,
                {
                  id: crypto.randomUUID(),
                  timestamp: formatTimestamp(),
                  category: event.note!.category,
                  content: event.note!.content,
                  question: event.note!.question,
                },
              ];
            });
          }
          break;

        case 'press_harder':
          if (event.criterion && event.message) {
            setPressHarder({
              criterion: event.criterion,
              message: event.message,
            });
            // Scroll to the live category
            liveCategoryRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
          break;

        case 'suggest_followup':
          if (event.followUpQuestion) {
            setPressHarder(null);
            setFollowUp({
              question: event.followUpQuestion,
              context: event.followUpContext ?? '',
              criterion: event.criterion ?? '',
            });
            setFollowUpVisible(true);
          }
          break;
      }
    },
    [formatTimestamp],
  );

  const { reset: resetTimeline } = useDemoTimeline({
    videoRef,
    events: activeScenario.events,
    onEvent: handleEvent,
    enabled: demoStarted && !demoEnded,
  });

  const startDemo = useCallback(() => {
    setDemoStarted(true);
    setDemoEnded(false);
    setIsPlaying(true);
  }, []);

  // Play video once the overlay has mounted after demoStarted flips to true
  useEffect(() => {
    if (!demoStarted || demoEnded) return;
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play().catch(() => {
      // Video not available — timeline falls back to wall-clock timer
    });
  }, [demoStarted, demoEnded]);

  const togglePlay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      await video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsPlaying(false);
    setDemoEnded(true);
  }, []);

  const resetDemo = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    setIsPlaying(false);
    setDemoStarted(false);
    setDemoEnded(false);
    setAnsweredQuestions({});
    setTranscriptLines([]);
    setAiNotes([]);
    setPressHarder(null);
    setFollowUp(null);
    setFollowUpVisible(false);
    resetTimeline();
  }, [resetTimeline]);

  const switchScenario = useCallback(
    (scenarioId: string) => {
      const scenario = DEMO_SCENARIOS.find((s) => s.id === scenarioId);
      if (!scenario || scenario.id === activeScenario.id) return;
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      setIsPlaying(false);
      setDemoStarted(false);
      setDemoEnded(false);
      setAnsweredQuestions({});
      setTranscriptLines([]);
      setAiNotes([]);
      setPressHarder(null);
      setFollowUp(null);
      setFollowUpVisible(false);
      resetTimeline();
      setActiveScenario(scenario);
    },
    [activeScenario.id, resetTimeline],
  );

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const pct = Math.min(80, Math.max(25, (x / rect.width) * 100));
      setLeftWidthPercent(pct);
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  // Auto-end demo after all timeline events have fired (fallback when no video)
  const lastEventTime =
    activeScenario.events[activeScenario.events.length - 1]?.timestamp ?? 0;
  useEffect(() => {
    if (!demoStarted || demoEnded) return;
    const timer = setTimeout(
      () => {
        setDemoEnded(true);
        setIsPlaying(false);
      },
      (lastEventTime + 5) * 1000,
    );
    return () => clearTimeout(timer);
  }, [demoStarted, demoEnded, lastEventTime]);

  // Group questions by category
  const questionsByCategory = activeScenario.questions.reduce(
    (acc, q) => {
      if (!acc[q.category]) acc[q.category] = [];
      acc[q.category].push(q);
      return acc;
    },
    {} as Record<string, DemoQuestion[]>,
  );

  const totalQuestions = activeScenario.questions.filter(
    (q) => !q.isFollowUp,
  ).length;
  const answeredCount = activeScenario.questions.filter(
    (q) => !q.isFollowUp && allAnswers[q.id],
  ).length;
  const liveAnsweredCount = Object.keys(answeredQuestions).length;

  // Determine which category contains the live question
  const liveCategory = activeScenario.questions.find(
    (q) => q.id === activeScenario.liveQuestionId,
  )?.category;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Banner */}
      <div className="border-b border-[#EAA059]/20 bg-gradient-to-r from-[#EAA059]/5 via-[#EAA059]/10 to-[#EAA059]/5">
        <div className="px-6 py-2.5 flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4914d] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <p className="text-sm text-foreground">
            <span className="font-semibold text-[#b47a3a]">
              AI-prepared questions
            </span>
            <span className="text-muted-foreground">
              {' '}
              — These questions were tailored by your FeedForward assistant based
              on the Acme Corp evaluation report.
            </span>
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm font-semibold text-foreground truncate">
                Acme Corp
              </span>
              <select
                value={activeScenario.id}
                onChange={(e) => switchScenario(e.target.value)}
                disabled={demoStarted && !demoEnded}
                className="px-2.5 py-1 border border-border rounded-lg bg-background text-foreground text-sm font-medium focus:border-[#EAA059] focus:outline-none focus:ring-1 focus:ring-[#EAA059]/20 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {DEMO_SCENARIOS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.criterion} — {s.criterionName}
                  </option>
                ))}
              </select>
              {demoStarted && (
                <div className="flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full flex-shrink-0">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs font-medium text-green-600 dark:text-green-400">
                    Live Call
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {!demoStarted ? (
                <Button
                  size="sm"
                  onClick={startDemo}
                  className="bg-[#D4893E] hover:bg-[#C07830] text-white gap-2 transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Start Demo
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetDemo}
                  className="gap-2 hover:bg-muted transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-3 mt-2">
            <div className="w-32 h-2.5 bg-muted/30 rounded-full overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${
                  answeredCount === totalQuestions
                    ? 'bg-emerald-500'
                    : 'bg-[#EAA059]'
                }`}
                animate={{
                  width: `${(answeredCount / totalQuestions) * 100}%`,
                }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
            <span className="text-sm font-bold text-foreground">
              {answeredCount}/{totalQuestions}
            </span>
            {liveAnsweredCount > 0 && (
              <div className="flex items-center gap-1 text-xs text-[#b47a3a]">
                <Sparkles className="w-3 h-3 text-[#EAA059]" />
                <span>{liveAnsweredCount} detected live</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        ref={containerRef}
        className="flex-1 flex flex-col md:flex-row overflow-hidden"
      >
        {/* Left — Questions Panel */}
        <div
          className="border-r border-[#EAA059]/15 bg-[#fdf8f3] dark:bg-card overflow-y-auto md:flex-none"
          style={{ width: `${leftWidthPercent}%` }}
        >
          <div className="p-4 pb-16">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-[#8b5e34] dark:text-[#EAA059]">
                Questions
              </h2>
              <span className="text-xs text-[#b47a3a] font-medium">
                {activeScenario.questions.filter((q) => !q.isFollowUp).length}{' '}
                questions
              </span>
            </div>

            <div className="space-y-2.5">
              {activeScenario.categoryOrder.map((category) => {
                const categoryQuestions = (
                  questionsByCategory[category] ?? []
                ).filter((q) => !q.isFollowUp);
                if (categoryQuestions.length === 0) return null;

                const categoryAnswered = categoryQuestions.filter(
                  (q) => allAnswers[q.id],
                ).length;
                const isExpanded = expandedCategories.has(category);
                const isLiveCategory = category === liveCategory;

                return (
                  <div
                    key={category}
                    ref={isLiveCategory ? liveCategoryRef : undefined}
                    className="rounded-xl overflow-hidden shadow-sm border border-[#EAA059]/20"
                  >
                    {/* Category header */}
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#EAA059] to-[#d4914d] hover:from-[#d4914d] hover:to-[#c48a50] transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-white/80" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-white/80" />
                        )}
                        <span className="font-semibold text-white">
                          {category}
                        </span>
                        {isLiveCategory && demoStarted && !demoEnded && (
                          <span className="relative flex h-2 w-2 ml-1">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm font-medium ${
                            categoryAnswered === categoryQuestions.length
                              ? 'text-white'
                              : 'text-white/70'
                          }`}
                        >
                          {categoryAnswered}/{categoryQuestions.length}
                        </span>
                        {categoryAnswered === categoryQuestions.length && (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </button>

                    {/* Questions */}
                    {isExpanded && (
                      <div className="divide-y divide-[#EAA059]/10">
                        {categoryQuestions.map((question) => {
                          const isLive =
                            !question.preAnswered &&
                            !question.isFollowUp;
                          const isAnswered = !!allAnswers[question.id];
                          const wasPreAnswered = !!preAnsweredMap[question.id];

                          return (
                            <div key={question.id}>
                              <QuestionRow
                                question={question}
                                answer={allAnswers[question.id]}
                                isAnswered={isAnswered}
                                isLiveAnswered={isAnswered && !wasPreAnswered}
                              />

                              {/* Follow-up suggestion (appears below the live question) */}
                              {question.id === activeScenario.liveQuestionId && (
                                <>
                                  {/* Press Harder Banner */}
                                  <AnimatePresence>
                                    {pressHarder && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="px-3 py-2"
                                      >
                                        <PressHarderBanner
                                          criterion={pressHarder.criterion}
                                          message={pressHarder.message}
                                          isVisible={true}
                                        />
                                      </motion.div>
                                    )}
                                  </AnimatePresence>

                                  {/* Follow-up suggestion + question */}
                                  <AnimatePresence>
                                    {followUpVisible && followUp && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{
                                          duration: 0.4,
                                          ease: 'easeOut',
                                        }}
                                      >
                                        <FollowUpCard
                                          followUp={followUp}
                                          question={activeScenario.questions.find(
                                            (q) => q.isFollowUp,
                                          )}
                                          answer={
                                            activeScenario.questions.find(
                                              (q) => q.isFollowUp,
                                            )
                                              ? allAnswers[
                                                  activeScenario.questions.find(
                                                    (q) => q.isFollowUp,
                                                  )!.id
                                                ]
                                              : undefined
                                          }
                                        />
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>
        </div>

        {/* Resize Handle */}
        <div
          onMouseDown={handleDragStart}
          className="hidden md:flex w-1.5 cursor-col-resize items-center justify-center hover:bg-[#EAA059]/10 active:bg-[#EAA059]/20 transition-colors group flex-shrink-0"
        >
          <div className="w-0.5 h-8 rounded-full bg-border group-hover:bg-[#EAA059]/40 group-active:bg-[#EAA059] transition-colors" />
        </div>

        {/* Right — Transcript + AI Notes */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Live Transcript */}
          <div className="border-b border-border">
            <div className="px-4 py-2.5 border-b border-border bg-muted/20">
              <div className="flex items-center gap-2">
                <Mic
                  className={`w-3.5 h-3.5 ${isPlaying ? 'text-green-500' : 'text-muted-foreground'}`}
                />
                <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Live Transcript
                </h2>
                {isPlaying && (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                  </span>
                )}
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto p-3 space-y-2">
              {transcriptLines.length === 0 ? (
                <p className="text-xs text-muted-foreground italic py-2 text-center">
                  {demoStarted
                    ? 'Listening...'
                    : 'Transcript will appear here when the demo starts.'}
                </p>
              ) : (
                transcriptLines.map((line) => (
                  <motion.div
                    key={line.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex gap-2 text-xs"
                  >
                    <span
                      className={`font-bold flex-shrink-0 ${
                        line.speaker === 'Maddie'
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-purple-600 dark:text-purple-400'
                      }`}
                    >
                      {line.speaker}:
                    </span>
                    <span className="text-foreground/80 leading-relaxed">
                      <StreamingText text={line.text} />
                    </span>
                  </motion.div>
                ))
              )}
            </div>
          </div>

          {/* AI Notes */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border bg-muted/20">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-[#EAA059]" />
                <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  AI Notes
                </h2>
                {aiNotes.length > 0 && (
                  <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                    {aiNotes.length}
                  </span>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {aiNotes.length === 0 ? (
                <div className="flex-1 flex items-center justify-center py-8">
                  <div className="text-center space-y-2">
                    <Bot className="w-8 h-8 text-muted-foreground/30 mx-auto" />
                    <p className="text-xs text-muted-foreground">
                      AI notes will appear as the call progresses.
                    </p>
                  </div>
                </div>
              ) : (
                aiNotes.map((note) => (
                  <motion.div
                    key={note.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={note.resolved ? { opacity: 0.5, y: 0 } : { opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className={`p-3 rounded-lg border shadow-sm transition-all ${
                      note.resolved
                        ? 'border-border/50 bg-muted/30'
                        : 'border-border bg-card'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                        {note.timestamp}
                      </span>
                      <FrameworkChip tag={note.category} />
                      {note.question && !note.resolved && (
                        <span className="text-[10px] font-bold text-white bg-[#EAA059] px-1.5 py-0.5 rounded uppercase tracking-wider">
                          Ask follow-up
                        </span>
                      )}
                      {note.resolved && (
                        <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30 px-1.5 py-0.5 rounded">
                          Resolved
                        </span>
                      )}
                    </div>
                    {note.question && (
                      <p
                        className={`text-[15px] font-semibold leading-snug mb-1.5 ${
                          note.resolved
                            ? 'text-muted-foreground line-through decoration-muted-foreground/40'
                            : 'text-foreground'
                        }`}
                      >
                        &ldquo;{note.question}&rdquo;
                      </p>
                    )}
                    <p
                      className={`text-sm leading-relaxed ${
                        note.resolved
                          ? 'text-muted-foreground line-through decoration-muted-foreground/40'
                          : note.question
                            ? 'text-muted-foreground'
                            : 'text-foreground'
                      }`}
                    >
                      {note.content}
                    </p>
                  </motion.div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Video Overlay */}
      <VideoOverlay
        ref={videoRef}
        src={activeScenario.videoSrc}
        isVisible={demoStarted}
        isPlaying={isPlaying}
        participantName="Erica — Acme Corp"
        onTogglePlay={togglePlay}
        onEnded={handleVideoEnded}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Question Row Component
// ---------------------------------------------------------------------------

function QuestionRow({
  question,
  answer,
  isAnswered,
  isLiveAnswered,
}: {
  question: DemoQuestion;
  answer?: string;
  isAnswered: boolean;
  isLiveAnswered: boolean;
}) {
  return (
    <div
      className={`transition-all ${
        isAnswered ? 'bg-white dark:bg-card' : 'bg-white dark:bg-card'
      }`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex-shrink-0 mt-0.5">
          <AnimatePresence mode="wait">
            {isAnswered ? (
              <motion.div
                key="checked"
                initial={isLiveAnswered ? { scale: 0 } : false}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 15 }}
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              </motion.div>
            ) : (
              <Circle className="w-5 h-5 text-[#EAA059]/40" />
            )}
          </AnimatePresence>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm leading-relaxed text-foreground">
              {question.question}
            </p>
            {question.frameworkTag && (
              <FrameworkChip tag={question.frameworkTag} />
            )}
          </div>
          {question.context && (
            <div className="flex items-start gap-1.5 mt-1.5">
              <Info className="w-3 h-3 text-[#d4914d] mt-0.5 flex-shrink-0" />
              <span className="text-xs leading-relaxed text-[#a07040]">
                {question.context}
              </span>
            </div>
          )}
          {isLiveAnswered && (
            <div className="flex items-center gap-1 mt-1">
              <Sparkles className="w-3 h-3 text-[#EAA059]" />
              <span className="text-xs text-[#b47a3a] font-medium">
                Detected live
              </span>
            </div>
          )}
          {isAnswered && !isLiveAnswered && (
            <div className="flex items-center gap-1 mt-1">
              <Sparkles className="w-3 h-3 text-emerald-500" />
              <span className="text-xs text-emerald-600 font-medium">
                Answered earlier
              </span>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {isAnswered && answer && (
          <motion.div
            initial={isLiveAnswered ? { opacity: 0, height: 0 } : false}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.3 }}
            className="px-4 pb-3 pl-12"
          >
            <div className="text-sm text-foreground leading-relaxed p-2.5 rounded-lg bg-[#faf6f1] dark:bg-muted/30 border border-[#EAA059]/15">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Follow-Up Card Component
// ---------------------------------------------------------------------------

function FollowUpCard({
  followUp,
  question,
  answer,
}: {
  followUp: FollowUpSuggestion;
  question?: DemoQuestion;
  answer?: string;
}) {
  const isAnswered = !!answer;

  return (
    <div className="border-l-3 border-l-[#EAA059] bg-[#fdf8f3] dark:bg-amber-950/10">
      <div className="px-4 py-3">
        <div className="flex items-center gap-1.5 mb-2.5">
          <Sparkles className="w-3.5 h-3.5 text-[#EAA059]" />
          <span className="text-xs font-bold text-[#b47a3a] uppercase tracking-wider">
            Suggested follow-up
          </span>
        </div>

        {/* Question — primary visual element */}
        {question && (
          <div className="flex items-start gap-2.5">
            <div className="flex-shrink-0 mt-0.5">
              <AnimatePresence mode="wait">
                {isAnswered ? (
                  <motion.div
                    key="checked"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', damping: 15 }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  </motion.div>
                ) : (
                  <Circle className="w-5 h-5 text-[#EAA059]/60" />
                )}
              </AnimatePresence>
            </div>
            <p className="text-[15px] font-semibold leading-snug text-foreground">
              &ldquo;{question.question}&rdquo;
            </p>
          </div>
        )}

        {/* AI observation — secondary context */}
        {followUp.context && (
          <p className="text-xs text-[#a07040] dark:text-amber-400/70 mt-2 leading-relaxed pl-[30px]">
            {followUp.context}
          </p>
        )}

        {question && isAnswered && (
          <div className="flex items-center gap-1 mt-1.5 pl-[30px]">
            <Sparkles className="w-3 h-3 text-[#EAA059]" />
            <span className="text-xs text-[#b47a3a] font-medium">
              Detected live
            </span>
          </div>
        )}
      </div>

      {question && (
        <AnimatePresence>
          {isAnswered && answer && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.3 }}
              className="px-4 pb-3 pl-[46px]"
            >
              <div className="text-sm text-foreground leading-relaxed p-2.5 rounded-lg bg-white dark:bg-muted/30 border border-[#EAA059]/15">
                {answer}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
